import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SparklesIcon, CheckCircleIcon, ExclamationCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { 
  generateRandomAccountInfo, 
  validateAccountInfo,
  formatCardNumber,
  validateCardNumberTemplate,
  formatCardTemplate,
  cleanCardTemplate,
  generateCompleteVirtualCard,
  identifyCardType,
  validateCardNumber,
  getSupportedCountries,
  getChinaProvinces,
  getChinaCities,
  getChinaDistricts,
  getOverseasStates,
  getOverseasCities,
  getOverseasDistricts,
  getPostalCodeHint,
  AccountInfo 
} from '../utils/generators';
import { useAppStore, getLastSavedText } from '../store/appStore';

const CardGeneration: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [appState, appStore] = useAppStore();
  
  // 局部状态（UI交互）
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [savedSuccessfully, setSavedSuccessfully] = useState(false);
  const [showDataSyncTip, setShowDataSyncTip] = useState(false);
  const [cardNumberError, setCardNumberError] = useState('');
  const [copySuccess, setCopySuccess] = useState('');
  const [luhnValidation, setLuhnValidation] = useState<boolean | null>(null);
  
  // 从全局store获取数据
  const { cardNumber, accountInfo, generatedCard, generatedExpiry, generatedCVV } = appState.cardGeneration;

  // 检查是否有其他页面的数据可以共享
  useEffect(() => {
    const { cardInformation } = appState;
    if (cardInformation.cardDetails.cardNumber && !generatedCard) {
      setShowDataSyncTip(true);
      setTimeout(() => setShowDataSyncTip(false), 8000);
    }
  }, [location, appState, generatedCard]);

  const handleRandomGenerate = async () => {
    setIsLoading(true);
    
    // 模拟API调用延迟
    await new Promise(resolve => setTimeout(resolve, 800));
    
    const randomInfo = generateRandomAccountInfo(accountInfo.countryCode || 'CN');
    appStore.updateCardGeneration({ accountInfo: randomInfo });
    setErrors({});
    setIsLoading(false);
  };

  const handleInputChange = (field: keyof AccountInfo, value: string) => {
    const newAccountInfo = { ...accountInfo, [field]: value };
    
    // 如果改变了国家，重置省市区
    if (field === 'country') {
      const country = getSupportedCountries().find(c => c.name === value);
      newAccountInfo.countryCode = country?.code || 'CN';
      newAccountInfo.province = '';
      newAccountInfo.city = '';
      newAccountInfo.district = '';
      newAccountInfo.postalCode = '';
    }
    
    // 如果改变了省份，重置城市和区县
    if (field === 'province') {
      newAccountInfo.city = '';
      newAccountInfo.district = '';
      newAccountInfo.postalCode = '';
    }
    
    // 如果改变了城市，重置区县
    if (field === 'city') {
      newAccountInfo.district = '';
      newAccountInfo.postalCode = '';
    }
    
    // 如果改变了区县，自动填充邮编
    if (field === 'district') {
      const countryCode = newAccountInfo.countryCode || 'CN';
      if (countryCode === 'CN') {
        const districts = getChinaDistricts(newAccountInfo.province, newAccountInfo.city);
        const selectedDistrict = districts.find(d => d.name === value);
        if (selectedDistrict) {
          newAccountInfo.postalCode = selectedDistrict.postalCode;
        }
      } else {
        const districts = getOverseasDistricts(countryCode, newAccountInfo.province, newAccountInfo.city);
        const selectedDistrict = districts.find(d => d.name === value);
        if (selectedDistrict) {
          newAccountInfo.postalCode = selectedDistrict.postalCode;
        }
      }
    }
    
    appStore.updateCardGeneration({ accountInfo: newAccountInfo });
    
    // 清除该字段的错误
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    setSavedSuccessfully(false);
  };

  // 获取省份/州列表
  const getProvinceOptions = () => {
    const countryCode = accountInfo.countryCode || 'CN';
    if (countryCode === 'CN') {
      return getChinaProvinces();
    }
    return getOverseasStates(countryCode);
  };

  // 获取城市列表
  const getCityOptions = () => {
    const countryCode = accountInfo.countryCode || 'CN';
    if (!accountInfo.province) return [];
    if (countryCode === 'CN') {
      return getChinaCities(accountInfo.province);
    }
    return getOverseasCities(countryCode, accountInfo.province);
  };

  // 获取区县列表
  const getDistrictOptions = () => {
    const countryCode = accountInfo.countryCode || 'CN';
    if (!accountInfo.province || !accountInfo.city) return [];
    if (countryCode === 'CN') {
      return getChinaDistricts(accountInfo.province, accountInfo.city);
    }
    return getOverseasDistricts(countryCode, accountInfo.province, accountInfo.city);
  };

  const handleCardNumberChange = (value: string) => {
    // 清理输入 - 只保留数字和x
    const cleaned = cleanCardTemplate(value);
    
    // 限制长度
    const limited = cleaned.substring(0, 19);
    
    // 验证格式
    const validation = validateCardNumberTemplate(limited);
    setCardNumberError(validation.error || '');
    
    // 更新状态
    appStore.updateCardGeneration({ cardNumber: limited });
    setSavedSuccessfully(false);
  };

  const handleGenerateCard = () => {
    // 先验证卡号段
    const validation = validateCardNumberTemplate(cardNumber);
    if (!validation.isValid) {
      setCardNumberError(validation.error || '卡号段格式错误');
      return;
    }
    
    // 生成完整虚拟卡信息
    const virtualCard = generateCompleteVirtualCard(cardNumber);
    appStore.updateCardGeneration({ 
      generatedCard: virtualCard.cardNumber,
      generatedExpiry: virtualCard.formattedExpiry,
      generatedCVV: virtualCard.cvv
    });
    setCardNumberError(''); // 清除错误
    
    // 验证生成的卡号是否通过 Luhn 算法
    const isValid = validateCardNumber(virtualCard.cardNumber);
    setLuhnValidation(isValid);

    // 保存到历史记录
    const cardType = identifyCardType(virtualCard.cardNumber);
    appStore.addHistory({
      cardNumber: virtualCard.cardNumber,
      expiry: virtualCard.formattedExpiry,
      cvv: virtualCard.cvv,
      cardType: cardType,
      accountInfo: accountInfo.holderName ? accountInfo : undefined,
    });
  };

  const handleCopy = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(`${type}已复制到剪贴板`);
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (err) {
      setCopySuccess('复制失败，请手动复制');
      setTimeout(() => setCopySuccess(''), 2000);
    }
  };

  const handleSave = async () => {
    // 验证卡号段
    const cardValidation = validateCardNumberTemplate(cardNumber);
    if (!cardValidation.isValid) {
      setCardNumberError(cardValidation.error || '卡号段格式错误');
      return;
    }
    
    // 验证表单
    const validation = validateAccountInfo(accountInfo);
    setErrors(validation.errors);
    
    if (!validation.isValid) {
      return;
    }
    
    setIsLoading(true);
    setSavedSuccessfully(false);
    
    // 模拟保存延迟
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // 保存到全局store
    appStore.save();
    
    setIsLoading(false);
    setSavedSuccessfully(true);
    
    // 3秒后隐藏成功提示
    setTimeout(() => setSavedSuccessfully(false), 3000);
  };

  const handleClear = () => {
    appStore.clearModule('cardGeneration');
    setErrors({});
    setSavedSuccessfully(false);
  };

  const handleModeChange = (mode: string) => {
    if (mode === 'complete') {
      navigate('/card-information');
    }
  };

  const syncFromCardInfo = () => {
    const { cardInformation } = appState;
    if (cardInformation.cardDetails.cardNumber) {
      appStore.updateCardGeneration({
        generatedCard: cardInformation.cardDetails.cardNumber
      });
      setShowDataSyncTip(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* 复制成功提示 */}
      {copySuccess && (
        <div className="fixed top-4 right-4 z-50 p-4 bg-green-600 text-white rounded-lg shadow-lg animate-pulse">
          <div className="flex items-center">
            <CheckCircleIcon className="h-5 w-5 mr-2" />
            <span className="font-medium">{copySuccess}</span>
          </div>
        </div>
      )}

      {/* 数据同步提示 */}
      {showDataSyncTip && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <ClockIcon className="h-5 w-5 text-blue-600 mr-2" />
              <span className="text-blue-800 font-medium">发现其他页面的卡号数据，是否同步到这里？</span>
            </div>
            <button 
              onClick={syncFromCardInfo}
              className="text-sm bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 transition-colors"
            >
              同步数据
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <CreditCardIcon className="h-6 w-6 mr-2 text-primary-600" />
            虚拟卡配置
          </h2>
          <div className="flex items-center space-x-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="cardMode"
                value="generation"
                checked={true}
                onChange={() => handleModeChange('generation')}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">卡号段生成</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="cardMode"
                value="complete"
                checked={false}
                onChange={() => handleModeChange('complete')}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-600">完整卡信息</span>
            </label>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="label">卡号段 (x为随机数字)</label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={cardNumber}
                onChange={(e) => handleCardNumberChange(e.target.value)}
                className={`input-field flex-1 ${cardNumberError ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="输入卡号段（例: 4xxx xxxx xxxx xxxx）"
                maxLength={23} // 19位 + 4个空格
              />
              <button
                onClick={handleGenerateCard}
                className="btn-primary whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading || !!cardNumberError || !cardNumber.trim()}
              >
                生成完整虚拟卡
              </button>
            </div>
            
            {/* 卡号段验证错误 */}
            {cardNumberError && (
              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                  {cardNumberError}
                </p>
              </div>
            )}
            
            {/* 卡号段格式化显示 */}
            {cardNumber && !cardNumberError && (
              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-700">
                  <span className="font-medium">格式化显示:</span> {formatCardTemplate(cardNumber)}
                </p>
              </div>
            )}
            {generatedCard && (
              <div className="mt-4 p-6 bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl shadow-sm">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-lg font-bold text-green-900">虚拟卡信息</h3>
                    {luhnValidation !== null && (
                      <div className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-full ${
                        luhnValidation 
                          ? 'bg-green-600 text-white' 
                          : 'bg-red-600 text-white'
                      }`}>
                        {luhnValidation ? (
                          <>
                            <CheckCircleIcon className="h-4 w-4" />
                            <span className="text-xs font-semibold">Luhn验证通过</span>
                          </>
                        ) : (
                          <>
                            <ExclamationCircleIcon className="h-4 w-4" />
                            <span className="text-xs font-semibold">Luhn验证失败</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        const cardInfo = `卡号: ${formatCardNumber(generatedCard)}\n有效期: ${generatedExpiry}\nCVV: ${generatedCVV}`;
                        handleCopy(cardInfo, '完整虚拟卡信息');
                      }}
                      className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-md hover:bg-green-700 transition-colors flex items-center space-x-1"
                      title="复制完整信息"
                    >
                      <span>复制全部</span>
                    </button>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-green-100 shadow-inner">
                  {/* 卡号 */}
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <label className="text-sm font-medium text-gray-700">卡号</label>
                      <button
                        onClick={() => handleCopy(generatedCard, '卡号')}
                        className="text-xs text-green-600 hover:text-green-800 underline"
                      >
                        复制
                      </button>
                    </div>
                    <p className="text-xl font-mono text-gray-900 tracking-wider bg-gray-50 p-3 rounded border">
                      {formatCardNumber(generatedCard)}
                    </p>
                  </div>
                  
                  {/* 有效期和CVV */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="text-sm font-medium text-gray-700">有效期</label>
                        <button
                          onClick={() => handleCopy(generatedExpiry, '有效期')}
                          className="text-xs text-green-600 hover:text-green-800 underline"
                        >
                          复制
                        </button>
                      </div>
                      <p className="text-lg font-mono text-gray-900 bg-gray-50 p-3 rounded border text-center">
                        {generatedExpiry}
                      </p>
                    </div>
                    
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="text-sm font-medium text-gray-700">CVV</label>
                        <button
                          onClick={() => handleCopy(generatedCVV, 'CVV')}
                          className="text-xs text-green-600 hover:text-green-800 underline"
                        >
                          复制
                        </button>
                      </div>
                      <p className="text-lg font-mono text-gray-900 bg-gray-50 p-3 rounded border text-center">
                        {generatedCVV}
                      </p>
                    </div>
                  </div>
                  
                  {/* 卡类型信息和 Luhn 验证详情 */}
                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                      <div className="flex items-center space-x-4">
                        <span>卡类型: <span className="font-medium">{identifyCardType(generatedCard)}</span></span>
                        <span>长度: <span className="font-medium">{generatedCard.length}位</span></span>
                      </div>
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">已生成</span>
                    </div>
                    {luhnValidation !== null && (
                      <div className={`mt-2 p-3 rounded-lg border ${
                        luhnValidation 
                          ? 'bg-green-50 border-green-300' 
                          : 'bg-red-50 border-red-300'
                      }`}>
                        <div className="flex items-start space-x-2">
                          {luhnValidation ? (
                            <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                          ) : (
                            <ExclamationCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                          )}
                          <div className="flex-1">
                            <p className={`text-sm font-semibold ${
                              luhnValidation ? 'text-green-900' : 'text-red-900'
                            }`}>
                              {luhnValidation ? '✓ Luhn 算法验证通过' : '✗ Luhn 算法验证失败'}
                            </p>
                            <p className={`text-xs mt-1 ${
                              luhnValidation ? 'text-green-700' : 'text-red-700'
                            }`}>
                              {luhnValidation 
                                ? '此卡号符合国际银行卡号标准（ISO/IEC 7812），校验位计算正确，可用于测试和开发。' 
                                : '此卡号未通过 Luhn 校验算法验证，可能无法在实际支付系统中使用。'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="mt-2 space-y-2">
              <div className="flex justify-between items-center text-sm">
                <div className="flex items-center space-x-4 text-gray-500">
                  <span>启动时卡将自动注册完整卡号，有效期和CVV</span>
                  <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                    支持: Visa, MasterCard, UnionPay, AmEx, Discover, JCB, Diners, Maestro, 借记卡(621-628)
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className={`font-medium px-2 py-1 rounded ${
                    cardNumber.replace(/\s/g, '').length >= 13 && cardNumber.replace(/\s/g, '').length <= 19 
                      ? 'text-green-700 bg-green-50' 
                      : 'text-red-700 bg-red-50'
                  }`}>
                    当前长度: {cardNumber.replace(/\s/g, '').length} 位
                  </span>
                </div>
              </div>
              {cardNumber && !cardNumberError && (
                <div className="p-2 bg-blue-50 border border-blue-200 rounded-md text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-blue-700">
                      <span className="font-medium">生成后卡号长度:</span> {cardNumber.replace(/\s/g, '').length} 位
                    </span>
                    <span className={`text-xs px-2 py-1 rounded font-medium ${
                      cardNumber.replace(/\s/g, '').length === 16 
                        ? 'bg-green-100 text-green-800' 
                        : cardNumber.replace(/\s/g, '').length === 15 || cardNumber.replace(/\s/g, '').length === 19
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {cardNumber.replace(/\s/g, '').length === 16 
                        ? '标准16位' 
                        : cardNumber.replace(/\s/g, '').length === 15 
                        ? 'AmEx 15位'
                        : cardNumber.replace(/\s/g, '').length === 19
                        ? 'UnionPay 19位'
                        : `${cardNumber.replace(/\s/g, '').length}位`}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <DocumentTextIcon className="h-6 w-6 mr-2 text-primary-600" />
            账单信息
          </h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                const billingInfo = `持卡人姓名: ${accountInfo.holderName}\n国家/地区: ${accountInfo.country}\n省/州: ${accountInfo.province}\n城市: ${accountInfo.city}\n区/县: ${accountInfo.district}\n邮编: ${accountInfo.postalCode}\n街道地址: ${accountInfo.street}\n地址第2行: ${accountInfo.addressLine2}`;
                handleCopy(billingInfo, '完整账单信息');
              }}
              className="btn-secondary text-sm flex items-center"
              title="复制完整账单信息"
            >
              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              复制全部
            </button>
            <button
              onClick={handleRandomGenerate}
              disabled={isLoading}
              className="btn-secondary flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <SparklesIcon className={`h-5 w-5 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              {isLoading ? '生成中...' : '随机生成'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label mb-0">持卡人姓名</label>
              <button
                onClick={() => handleCopy(accountInfo.holderName, '持卡人姓名')}
                className="text-xs text-primary-600 hover:text-primary-800 underline flex items-center"
                title="复制持卡人姓名"
              >
                <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                复制
              </button>
            </div>
            <input
              type="text"
              value={accountInfo.holderName}
              onChange={(e) => handleInputChange('holderName', e.target.value)}
              className={`input-field ${errors.holderName ? 'border-red-500 focus:ring-red-500' : ''}`}
              placeholder="输入持卡人姓名"
            />
            {errors.holderName && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                {errors.holderName}
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label mb-0">国家/地区</label>
              <button
                onClick={() => handleCopy(accountInfo.country, '国家/地区')}
                className="text-xs text-primary-600 hover:text-primary-800 underline flex items-center"
                title="复制国家/地区"
              >
                <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                复制
              </button>
            </div>
            <select
              value={accountInfo.country}
              onChange={(e) => handleInputChange('country', e.target.value)}
              className="input-field"
            >
              {getSupportedCountries().map(country => (
                <option key={country.code} value={country.name}>{country.name}</option>
              ))}
            </select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label mb-0">省/州</label>
              <button
                onClick={() => handleCopy(accountInfo.province, '省/州')}
                className="text-xs text-primary-600 hover:text-primary-800 underline flex items-center"
                title="复制省/州"
              >
                <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                复制
              </button>
            </div>
            <select
              value={accountInfo.province}
              onChange={(e) => handleInputChange('province', e.target.value)}
              className={`input-field ${errors.province ? 'border-red-500 focus:ring-red-500' : ''}`}
            >
              <option value="">请选择省/州</option>
              {getProvinceOptions().map(province => (
                <option key={province} value={province}>{province}</option>
              ))}
            </select>
            {errors.province && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                {errors.province}
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label mb-0">城市</label>
              <button
                onClick={() => handleCopy(accountInfo.city, '城市')}
                className="text-xs text-primary-600 hover:text-primary-800 underline flex items-center"
                title="复制城市"
              >
                <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                复制
              </button>
            </div>
            <select
              value={accountInfo.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              className={`input-field ${errors.city ? 'border-red-500 focus:ring-red-500' : ''}`}
              disabled={!accountInfo.province}
            >
              <option value="">请选择城市</option>
              {getCityOptions().map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
            {errors.city && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                {errors.city}
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label mb-0">区/县</label>
              <button
                onClick={() => handleCopy(accountInfo.district, '区/县')}
                className="text-xs text-primary-600 hover:text-primary-800 underline flex items-center"
                title="复制区/县"
              >
                <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                复制
              </button>
            </div>
            <select
              value={accountInfo.district}
              onChange={(e) => handleInputChange('district', e.target.value)}
              className={`input-field ${errors.district ? 'border-red-500 focus:ring-red-500' : ''}`}
              disabled={!accountInfo.city}
            >
              <option value="">请选择区/县</option>
              {getDistrictOptions().map(district => (
                <option key={district.name} value={district.name}>{district.name}</option>
              ))}
            </select>
            {errors.district && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                {errors.district}
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label mb-0">邮编</label>
              <button
                onClick={() => handleCopy(accountInfo.postalCode, '邮编')}
                className="text-xs text-primary-600 hover:text-primary-800 underline flex items-center"
                title="复制邮编"
              >
                <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                复制
              </button>
            </div>
            <input
              type="text"
              value={accountInfo.postalCode}
              onChange={(e) => handleInputChange('postalCode', e.target.value)}
              className={`input-field ${errors.postalCode ? 'border-red-500 focus:ring-red-500' : ''}`}
              placeholder={getPostalCodeHint(accountInfo.countryCode || 'CN')}
            />
            {errors.postalCode && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                {errors.postalCode}
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label mb-0">街道地址</label>
              <button
                onClick={() => handleCopy(accountInfo.street, '街道地址')}
                className="text-xs text-primary-600 hover:text-primary-800 underline flex items-center"
                title="复制街道地址"
              >
                <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                复制
              </button>
            </div>
            <input
              type="text"
              value={accountInfo.street}
              onChange={(e) => handleInputChange('street', e.target.value)}
              className={`input-field ${errors.street ? 'border-red-500 focus:ring-red-500' : ''}`}
              placeholder="输入街道地址"
            />
            {errors.street && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                {errors.street}
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label mb-0">地址第2行 (选填)</label>
              <button
                onClick={() => handleCopy(accountInfo.addressLine2, '地址第2行')}
                className="text-xs text-primary-600 hover:text-primary-800 underline flex items-center"
                title="复制地址第2行"
              >
                <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                复制
              </button>
            </div>
            <input
              type="text"
              value={accountInfo.addressLine2}
              onChange={(e) => handleInputChange('addressLine2', e.target.value)}
              className="input-field"
              placeholder="输入详细地址"
            />
          </div>
        </div>

        {/* 保存成功提示 */}
        {savedSuccessfully && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-green-800 font-medium">配置保存成功！</span>
              </div>
              <span className="text-sm text-green-600">
                {getLastSavedText(appState.cardGeneration.lastSaved)}
              </span>
            </div>
          </div>
        )}

        {/* 状态显示栏 */}
        <div className="mt-6 flex items-center justify-between text-sm text-gray-500 bg-gray-50 px-4 py-3 rounded-lg">
          <div className="flex items-center space-x-4">
            <span>数据状态: {appState.cardGeneration.lastSaved ? '已保存' : '未保存'}</span>
            {appState.cardGeneration.lastSaved && (
              <span className="flex items-center">
                <ClockIcon className="h-4 w-4 mr-1" />
                {getLastSavedText(appState.cardGeneration.lastSaved)}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <span>自动保存: {appState.settings.autoSave ? '开启' : '关闭'}</span>
            <div className={`w-2 h-2 rounded-full ${
              appState.settings.autoSave ? 'bg-green-500' : 'bg-gray-400'
            }`}></div>
          </div>
        </div>

        <div className="mt-8 flex justify-between items-center">
          <div className="flex space-x-2">
            <button 
              onClick={handleClear}
              className="btn-secondary"
              disabled={isLoading}
            >
              清空
            </button>
            <button 
              onClick={() => appStore.updateSettings({ autoSave: !appState.settings.autoSave })}
              className="btn-secondary text-sm"
            >
              {appState.settings.autoSave ? '关闭自动保存' : '开启自动保存'}
            </button>
          </div>
          <button 
            onClick={handleSave}
            disabled={isLoading}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isLoading ? (
              <>
                <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                保存中...
              </>
            ) : (
              '保存配置'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const CreditCardIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
  </svg>
);

const DocumentTextIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

export default CardGeneration;
